import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';
import { ProcessingImage } from '../../processing-image';

declare const Speedy: any;

/**
 * Speedy Vision engine — GPU-accelerated computer vision via WebGL.
 * Uses speedy-vision.js (https://github.com/alemart/speedy-vision)
 * loaded from CDN. Provides real-time feature detection, tracking,
 * and image processing operations.
 */
export class SpeedyVisionEngine implements ProcessingEngine {
  readonly id = 'speedy-vision';
  readonly name = 'Speedy Vision (WebGL)';
  readonly operations: OperationDescriptor[] = [
    {
      id: 'greyscale',
      name: 'Greyscale',
      engineId: 'speedy-vision',
      category: 'enhancement',
      description: 'Convert image to greyscale using GPU',
      runsOn: 'client',
      params: [],
    },
    {
      id: 'gaussian-blur',
      name: 'Gaussian Blur',
      engineId: 'speedy-vision',
      category: 'filter',
      description: 'GPU-accelerated Gaussian blur',
      runsOn: 'client',
      params: [
        { id: 'kernelSize', label: 'Kernel Size', type: 'select', defaultValue: '5',
          options: [
            { label: '3x3', value: '3' },
            { label: '5x5', value: '5' },
            { label: '7x7', value: '7' },
          ] },
      ],
    },
    {
      id: 'median-blur',
      name: 'Median Filter',
      engineId: 'speedy-vision',
      category: 'filter',
      description: 'GPU-accelerated median filter for noise reduction',
      runsOn: 'client',
      params: [
        { id: 'kernelSize', label: 'Kernel Size', type: 'select', defaultValue: '5',
          options: [
            { label: '3x3', value: '3' },
            { label: '5x5', value: '5' },
            { label: '7x7', value: '7' },
          ] },
      ],
    },
    {
      id: 'box-blur',
      name: 'Box Filter',
      engineId: 'speedy-vision',
      category: 'filter',
      description: 'GPU-accelerated box (mean) filter',
      runsOn: 'client',
      params: [
        { id: 'kernelSize', label: 'Kernel Size', type: 'select', defaultValue: '5',
          options: [
            { label: '3x3', value: '3' },
            { label: '5x5', value: '5' },
            { label: '7x7', value: '7' },
            { label: '9x9', value: '9' },
          ] },
      ],
    },
    {
      id: 'convolution',
      name: 'Custom Convolution',
      engineId: 'speedy-vision',
      category: 'filter',
      description: 'Apply a custom 3x3 convolution kernel',
      runsOn: 'client',
      params: [
        { id: 'preset', label: 'Kernel Preset', type: 'select', defaultValue: 'sharpen',
          options: [
            { label: 'Sharpen', value: 'sharpen' },
            { label: 'Emboss', value: 'emboss' },
            { label: 'Edge Detect', value: 'edge' },
            { label: 'Laplacian', value: 'laplacian' },
            { label: 'Ridge', value: 'ridge' },
          ] },
      ],
    },
    {
      id: 'normalize',
      name: 'Normalize',
      engineId: 'speedy-vision',
      category: 'enhancement',
      description: 'Normalize image intensity to fill full dynamic range',
      runsOn: 'client',
      params: [
        { id: 'minValue', label: 'Min Value', type: 'number', defaultValue: 0, min: 0, max: 254, step: 1 },
        { id: 'maxValue', label: 'Max Value', type: 'number', defaultValue: 255, min: 1, max: 255, step: 1 },
      ],
    },
    {
      id: 'brightness-contrast',
      name: 'Brightness & Contrast',
      engineId: 'speedy-vision',
      category: 'enhancement',
      description: 'Adjust brightness and contrast using GPU',
      runsOn: 'client',
      params: [
        { id: 'brightness', label: 'Brightness', type: 'number', defaultValue: 0, min: -255, max: 255, step: 5 },
        { id: 'contrast', label: 'Contrast', type: 'number', defaultValue: 0, min: -255, max: 255, step: 5 },
      ],
    },
    {
      id: 'harris',
      name: 'Harris Corners',
      engineId: 'speedy-vision',
      category: 'detection',
      description: 'Detect Harris corners (keypoints drawn on image)',
      runsOn: 'client',
      params: [
        { id: 'quality', label: 'Quality', type: 'number', defaultValue: 0.1, min: 0.01, max: 1.0, step: 0.01 },
        { id: 'maxFeatures', label: 'Max Features', type: 'number', defaultValue: 500, min: 10, max: 5000, step: 50 },
      ],
    },
    {
      id: 'fast',
      name: 'FAST Features',
      engineId: 'speedy-vision',
      category: 'detection',
      description: 'Detect FAST feature points (keypoints drawn on image)',
      runsOn: 'client',
      params: [
        { id: 'threshold', label: 'Threshold', type: 'number', defaultValue: 20, min: 1, max: 255, step: 1 },
        { id: 'maxFeatures', label: 'Max Features', type: 'number', defaultValue: 500, min: 10, max: 5000, step: 50 },
      ],
    },
    {
      id: 'orb',
      name: 'ORB Features',
      engineId: 'speedy-vision',
      category: 'detection',
      description: 'Detect ORB feature points with descriptors (keypoints drawn on image)',
      runsOn: 'client',
      params: [
        { id: 'maxFeatures', label: 'Max Features', type: 'number', defaultValue: 300, min: 10, max: 3000, step: 50 },
      ],
    },
    {
      id: 'warp-perspective',
      name: 'Perspective Warp',
      engineId: 'speedy-vision',
      category: 'transform',
      description: 'Apply a perspective warp (rotation around center)',
      runsOn: 'client',
      params: [
        { id: 'angle', label: 'Angle (degrees)', type: 'number', defaultValue: 15, min: -180, max: 180, step: 1 },
        { id: 'scale', label: 'Scale', type: 'number', defaultValue: 1.0, min: 0.1, max: 3.0, step: 0.1 },
      ],
    },
    {
      id: 'nightvision',
      name: 'Nightvision',
      engineId: 'speedy-vision',
      category: 'enhancement',
      description: 'Nightvision-style enhancement (normalize + greyscale + gain)',
      runsOn: 'client',
      params: [
        { id: 'gain', label: 'Gain', type: 'number', defaultValue: 2.0, min: 0.5, max: 5.0, step: 0.1 },
      ],
    },
  ];

  private speedyReady = false;
  private scriptLoaded = false;

  isInitialized(): boolean {
    return this.speedyReady;
  }

  async initialize(): Promise<void> {
    if (this.speedyReady) return;
    await this.loadScript();
    this.speedyReady = true;
  }

  async execute(
    operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage> {
    if (!this.speedyReady) await this.initialize();

    const canvas = this.imageToCanvas(input);
    const media = await Speedy.load(canvas);

    try {
      let resultCanvas: HTMLCanvasElement;

      switch (operation.id) {
        case 'greyscale':
          resultCanvas = await this.runFilter(media, 'greyscale');
          break;
        case 'gaussian-blur':
          resultCanvas = await this.runFilter(media, 'gaussian', { size: this.toKernelSize(params.kernelSize) });
          break;
        case 'median-blur':
          resultCanvas = await this.runFilter(media, 'median', { size: this.toKernelSize(params.kernelSize) });
          break;
        case 'box-blur':
          resultCanvas = await this.runFilter(media, 'box', { size: this.toKernelSize(params.kernelSize) });
          break;
        case 'convolution':
          resultCanvas = await this.runConvolution(media, params.preset || 'sharpen');
          break;
        case 'normalize':
          resultCanvas = await this.runNormalize(media, params.minValue ?? 0, params.maxValue ?? 255);
          break;
        case 'brightness-contrast':
          resultCanvas = await this.runBrightnessContrast(media, params.brightness ?? 0, params.contrast ?? 0);
          break;
        case 'harris':
          resultCanvas = await this.runFeatureDetection(media, 'harris', input, params);
          break;
        case 'fast':
          resultCanvas = await this.runFeatureDetection(media, 'fast', input, params);
          break;
        case 'orb':
          resultCanvas = await this.runFeatureDetection(media, 'orb', input, params);
          break;
        case 'warp-perspective':
          resultCanvas = await this.runWarp(media, params.angle ?? 15, params.scale ?? 1.0);
          break;
        case 'nightvision':
          resultCanvas = await this.runNightvision(media, params.gain ?? 2.0);
          break;
        default:
          throw new Error(`SpeedyVision: unknown operation "${operation.id}"`);
      }

      return this.canvasToImage(resultCanvas);
    } finally {
      media.release();
    }
  }

  dispose(): void {
    this.speedyReady = false;
  }

  // --- Private helpers ---

  private async loadScript(): Promise<void> {
    if (this.scriptLoaded) return;
    if (typeof Speedy !== 'undefined') {
      this.scriptLoaded = true;
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/alemart/speedy-vision@0.9.1/dist/speedy-vision.min.js';
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load speedy-vision.js from CDN'));
      document.head.appendChild(script);
    });
  }

  private imageToCanvas(image: ProcessingImage): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d')!;
    ctx.putImageData(image.toImageData(), 0, 0);
    return canvas;
  }

  private canvasToImage(canvas: HTMLCanvasElement): ProcessingImage {
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return ProcessingImage.fromImageData(imageData);
  }

  private async runFilter(media: any, filterName: string, options: any = {}): Promise<HTMLCanvasElement> {
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const filter = Speedy.Filter[this.capitalize(filterName)](options);
    const sink = Speedy.Image.Sink();

    source.media = media;
    source.output().connectTo(filter.input());
    filter.output().connectTo(sink.input());

    pipeline.init(source, filter, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  private async runConvolution(media: any, preset: string): Promise<HTMLCanvasElement> {
    const kernel = this.getConvolutionKernel(preset);
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const conv = Speedy.Filter.Convolution({ kernel });
    const sink = Speedy.Image.Sink();

    source.media = media;
    source.output().connectTo(conv.input());
    conv.output().connectTo(sink.input());

    pipeline.init(source, conv, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  private async runBrightnessContrast(media: any, brightness: number,
                                      contrast: number): Promise<HTMLCanvasElement> {
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const greyscale = Speedy.Filter.Greyscale();
    const adjust = Speedy.Filter.Normalize({
      minValue: Math.max(0, 0 - brightness + contrast),
      maxValue: Math.min(255, 255 + brightness - contrast),
    });
    const sink = Speedy.Image.Sink();

    source.media = media;
    source.output().connectTo(greyscale.input());
    greyscale.output().connectTo(adjust.input());
    adjust.output().connectTo(sink.input());

    pipeline.init(source, greyscale, adjust, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  /** Normalize requires greyscale input — converts automatically. */
  private async runNormalize(media: any, minValue: number, maxValue: number): Promise<HTMLCanvasElement> {
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const greyscale = Speedy.Filter.Greyscale();
    const normalize = Speedy.Filter.Normalize({ minValue, maxValue });
    const sink = Speedy.Image.Sink();

    source.media = media;
    source.output().connectTo(greyscale.input());
    greyscale.output().connectTo(normalize.input());
    normalize.output().connectTo(sink.input());

    pipeline.init(source, greyscale, normalize, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  private async runFeatureDetection(
    media: any,
    detector: string,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<HTMLCanvasElement> {
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const greyscale = Speedy.Filter.Greyscale();
    const maxFeatures = params.maxFeatures ?? 500;
    let det: any;

    if (detector === 'harris') {
      det = Speedy.Keypoint.Detector.Harris({
        quality: params.quality ?? 0.1,
        capacity: maxFeatures * 2,
      });
    } else if (detector === 'fast') {
      det = Speedy.Keypoint.Detector.FAST({
        threshold: params.threshold ?? 20,
        capacity: maxFeatures * 2,
      });
    } else {
      // ORB in speedy-vision = FAST detector + ORB descriptor
      det = Speedy.Keypoint.Detector.FAST({
        threshold: params.threshold ?? 20,
        capacity: maxFeatures * 2,
      });
    }

    // Clipper enforces the maxFeatures limit by keeping only the strongest keypoints
    const clipper = Speedy.Keypoint.Clipper({ size: maxFeatures });
    const kpSink = Speedy.Keypoint.Sink();

    source.media = media;
    source.output().connectTo(greyscale.input());
    greyscale.output().connectTo(det.input());

    if (detector === 'orb') {
      // ORB descriptor needs both the greyscale image and keypoints as separate inputs
      const descriptor = Speedy.Keypoint.Descriptor.ORB();
      greyscale.output().connectTo(descriptor.input('image'));
      det.output().connectTo(descriptor.input('keypoints'));
      descriptor.output().connectTo(clipper.input());
      clipper.output().connectTo(kpSink.input());
      pipeline.init(source, greyscale, det, descriptor, clipper, kpSink);
    } else {
      det.output().connectTo(clipper.input());
      clipper.output().connectTo(kpSink.input());
      pipeline.init(source, greyscale, det, clipper, kpSink);
    }
    const { keypoints } = await pipeline.run().finally(() => pipeline.release());

    // Draw keypoints on the original image
    const canvas = this.imageToCanvas(input);
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#00ff00';
    for (const kp of keypoints) {
      ctx.beginPath();
      ctx.arc(kp.x, kp.y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }
    return canvas;
  }

  private async runWarp(media: any, angle: number, scale: number): Promise<HTMLCanvasElement> {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad) * scale;
    const sin = Math.sin(rad) * scale;
    const cx = media.width / 2;
    const cy = media.height / 2;

    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const warp = Speedy.Transform.PerspectiveWarp();
    const sink = Speedy.Image.Sink();

    const tx = cx - cos * cx + sin * cy;
    const ty = cy - sin * cx - cos * cy;
    warp.transform = Speedy.Matrix(3, 3, [
      cos, -sin, tx,
      sin, cos, ty,
      0, 0, 1,
    ]);

    source.media = media;
    source.output().connectTo(warp.input());
    warp.output().connectTo(sink.input());

    pipeline.init(source, warp, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  private async runNightvision(media: any, gain: number): Promise<HTMLCanvasElement> {
    const pipeline = Speedy.Pipeline();
    const source = Speedy.Image.Source();
    const greyscale = Speedy.Filter.Greyscale();
    const normalize = Speedy.Filter.Normalize({ minValue: 0, maxValue: Math.min(255, Math.round(255 * gain)) });
    const sink = Speedy.Image.Sink();

    source.media = media;
    source.output().connectTo(greyscale.input());
    greyscale.output().connectTo(normalize.input());
    normalize.output().connectTo(sink.input());

    pipeline.init(source, greyscale, normalize, sink);
    try {
      const { image } = await pipeline.run();
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(await image.toBitmap(), 0, 0);
      return canvas;
    } finally {
      pipeline.release();
    }
  }

  private getConvolutionKernel(preset: string): any {
    switch (preset) {
      case 'sharpen':
        return Speedy.Matrix(3, 3, [0, -1, 0, -1, 5, -1, 0, -1, 0]);
      case 'emboss':
        return Speedy.Matrix(3, 3, [-2, -1, 0, -1, 1, 1, 0, 1, 2]);
      case 'edge':
        return Speedy.Matrix(3, 3, [-1, -1, -1, -1, 8, -1, -1, -1, -1]);
      case 'laplacian':
        return Speedy.Matrix(3, 3, [0, 1, 0, 1, -4, 1, 0, 1, 0]);
      case 'ridge':
        return Speedy.Matrix(3, 3, [-1, -1, -1, -1, 9, -1, -1, -1, -1]);
      default:
        return Speedy.Matrix(3, 3, [0, 0, 0, 0, 1, 0, 0, 0, 0]);
    }
  }

  private toKernelSize(value: any): { width: number; height: number } {
    const size = parseInt(value, 10) || 5;
    return { width: size, height: size };
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
